import Button from "components/base/Button";
import { DateInput } from "components/base/Form";
import SearchBar from "components/base/SearchBar";
import { useModals } from "ModalProvider";
import moment from "moment";
import OdooCustomersView from "pages/retailer/retailers/OdooCustomersView";
import { createContext, useRef, useState } from "react";
import { BsFilterCircle, BsFilterCircleFill } from "react-icons/bs";
import { IoIosCloseCircleOutline } from "react-icons/io";
import { useSearchParams } from "react-router-dom";
import OdooMarginTableView from "./OdooMarginTableView";

export const OdooMarginContext = createContext();

const OdooInvoiceMarginPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const modal = useModals();

  const startDate =
    searchParams.get("start") ||
    moment(new Date(new Date().setMonth(new Date().getMonth() - 1))).format(
      "YYYY-MM-DD",
    );
  const endDate =
    searchParams.get("end") || moment(new Date()).format("YYYY-MM-DD");
  const searchText = searchParams.get("searchText") || "";

  const excludeCustomerString = searchParams.get("excludeCustomerString") || "";
  const excludeCustomers = excludeCustomerString.split(",").filter(Boolean);
  const excludePartnerIds = excludeCustomers
    .map((customer) => Number(customer.split("#")[0]))
    .filter((id) => Number.isInteger(id));

  const ref = useRef();

  const [showFilters, setShowFilters] = useState(excludeCustomers.length > 0);

  const params = Object.fromEntries(searchParams.entries());

  function showOdooCustomers() {
    modal.present({
      title: "Choose Customer",
      children: (
        <OdooCustomersView
          instance="AB"
          useType="SIMPLE"
          onSelect={onSelectOdooCustomer}
        />
      ),
    });
  }

  function onSelectOdooCustomer(odooCustomer) {
    modal.hide();
    const newCustomer = `${odooCustomer.id}#${odooCustomer.name}`;
    if (!excludeCustomers.includes(newCustomer)) {
      setSearchParams({
        ...params,
        excludeCustomerString: [
          ...excludeCustomers,
          `${odooCustomer.id}#${odooCustomer.name}`,
        ].join(","),
      });
    }
  }

  function handleRemoveCustomer(customer) {
    const newCustomers = excludeCustomers.filter((c) => c !== customer);
    setSearchParams({
      ...params,
      excludeCustomerString: newCustomers.join(","),
    });
  }

  return (
    <div className="flex flex-col overflow-auto p-6 pt-4 h-full space-y-2">
      <div className="flex items-center space-x-3 divide-x divide-gray-300 dark:divide-gray-700">
        <div className="flex items-center space-x-3 flex-1">
          <label htmlFor="">From</label>
          <DateInput
            value={startDate}
            onChange={(e) =>
              setSearchParams({ ...params, start: e.target.value })
            }
          />
          <label htmlFor="">to</label>
          <DateInput
            value={endDate}
            onChange={(e) =>
              setSearchParams({ ...params, end: e.target.value })
            }
          />

          <div className="flex-1">
            <SearchBar
              value={searchText}
              onChange={(text) =>
                setSearchParams({ ...params, searchText: text })
              }
              placeholder="Search by invoice number, ref or customer name."
            />
          </div>

          <Button onClick={() => setShowFilters((prev) => !prev)}>
            {showFilters ? <BsFilterCircleFill /> : <BsFilterCircle />}
          </Button>
        </div>

        <div className="pl-3">
          <Button
            onClick={() => {
              if (ref.current) ref.current.download();
            }}
          >
            Download
          </Button>
        </div>
      </div>

      {showFilters && (
        <div className="flex items-baseline space-x-3">
          <label>Exclude Customer:</label>

          {excludeCustomers.length > 0 && (
            <div className="flex space-x-1 overflow-auto whitespace-nowrap flex-wrap">
              {excludeCustomers.map((customer, index) => (
                <div key={index} className="flex items-center mr-2 space-x-1">
                  <span>{customer.split("#")[1]}</span>
                  <Button onClick={() => handleRemoveCustomer(customer)} danger>
                    <IoIosCloseCircleOutline />
                  </Button>
                </div>
              ))}
            </div>
          )}

          <Button onClick={showOdooCustomers}>Choose</Button>
        </div>
      )}

      <div className="relative flex-1 card p-0 overflow-auto">
        <OdooMarginTableView
          ref={ref}
          searchText={searchText}
          startDate={moment(startDate, "YYYY-MM-DD")}
          endDate={moment(endDate, "YYYY-MM-DD")}
          excludePartnerIds={excludePartnerIds}
        />
      </div>

      <div className="mt-4 opacity-70 mx-1">
        This page shows the margin of each invoice. The margin is calculated as
        follows: 1. Total credit for account starts with 3 - total debit for
        accounts starts with 4051 or 4054 / total credit for account starts with
        3 * 100. 2. If the total credit for account starts with 3 is 0, the
        margin is 0.
      </div>
    </div>
  );
};

export default OdooInvoiceMarginPage;
